import { useState } from "react";
import { PnlTimes } from "../PnLChart/pnlChartTypes";
import BreakdownRow from "./BreakdownRow";
import { PnlBreakdown } from "../../../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import {
  MetricAndPnLBreakdownStyles,
  SkuBreakdownStyles,
} from "./metricAndPnLBreakdownStyles";

import {
  getSummaryMetrics,
  sortByOrder,
  SummaryMetricInput,
  useSummaryMetrics,
} from "reports/utils/metricUtils";
import { Collapse, useMediaQuery } from "@mui/material";
import ProcessPnLBreakdown from "./ProcessPnLBreakdown";
import { BreakdownTypeEnum } from "./metricAndPnlBreakdownTypes";
import ReportButton from "../../Buttons/ReportButton";
import useEosData from "reports/store/useEosData";
import { useTranslation } from "react-i18next";
import { useTargetsFromCustomerJson } from "reports/utils/customerInfoUtils";
import { getPnlTimes } from "../PnLChart/pnlChartUtils";
import { SummaryMetricType } from "../../SummaryMetricsRow";
import { BREAKPOINT_MOBILE } from "constants/constants";

export default function BreakdownRowForShiftAndProcess({
  showMetricTitle,
  breakdownName,
  pnlBreakdown,
  pnlTimes,
  summaryMetricsInputList,
  getCustomSummaryMetricsInputListWithPnlBreakdown,
  maxBreakdownHeaderLength,
  shouldExpand = true,
  shouldShowButterfly = true,
  reportLink,
  shouldEnableToggle = true,
}: {
  showMetricTitle: boolean;
  breakdownName: string;
  pnlBreakdown: PnlBreakdown;
  pnlTimes: PnlTimes[];
  summaryMetricsInputList: SummaryMetricInput[];
  getCustomSummaryMetricsInputListWithPnlBreakdown?: (
    pnlBreakdown: PnlBreakdown
  ) => SummaryMetricInput[];
  maxBreakdownHeaderLength: number;
  shouldExpand?: boolean;
  shouldShowButterfly?: boolean;
  reportLink?: string;
  shouldEnableToggle?: boolean;
}) {
  const { t } = useTranslation("EOSReport");
  const targets = useTargetsFromCustomerJson();
  const { pnlBreakdownByShiftAndProcess } = useEosData();

  const processPnlBreakdown = pnlBreakdownByShiftAndProcess.filter(
    breakdown => breakdown.shiftName == pnlBreakdown.shiftName
  );

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = () => {
    shouldExpand && setIsExpanded(!isExpanded);
  };
  const useShortName = true; // always use short name in MachineBreakdown

  const getCustomAndDefaultSummaryMetricsInputList = (
    pnlBreakdown: PnlBreakdown
  ) =>
    summaryMetricsInputList.concat(
      getCustomSummaryMetricsInputListWithPnlBreakdown
        ? getCustomSummaryMetricsInputListWithPnlBreakdown(pnlBreakdown)
        : []
    );

  const summaryMetrics = useSummaryMetrics({
    metrics: getCustomAndDefaultSummaryMetricsInputList(pnlBreakdown),
    pnlBreakdown,
    useShortName,
  }).sort(sortByOrder);

  const isMobileOrSmaller = useMediaQuery(
    `(max-width: ${BREAKPOINT_MOBILE}px)`
  );

  const processPnlBreakdownRowData = processPnlBreakdown.map(processData => {
    const metrics =
      getCustomAndDefaultSummaryMetricsInputList(processData).sort(sortByOrder);
    const processSummarySummaryMetrics = getSummaryMetrics({
      t,
      targets,
      metrics,
      pnlBreakdown: processData,
      useShortName,
    });
    const processPnlBreakdown = getPnlTimes({ data: processData });
    return {
      processName: processData.processNames[0],
      processSummarySummaryMetrics,
      processPnlBreakdown,
      range: {
        ge: processData.ge,
        lt: processData.lt,
      },
      processId: processData.machemetryIds[0],
    } as {
      processName: string;
      processSummarySummaryMetrics: SummaryMetricType[];
      processPnlBreakdown: PnlTimes[];
      range: {
        ge: number;
        lt: number;
      };
      processId: string;
    };
  });

  return (
    <>
      <MetricAndPnLBreakdownStyles
        {...{
          $isExpanded: isExpanded,
          $shouldShowButterfly: shouldShowButterfly,
          $shouldExpand: shouldExpand,
        }}
        onClick={shouldEnableToggle ? toggleExpand : undefined}
      >
        <BreakdownRow
          breakdownName={breakdownName}
          breakdownSummaryMetrics={summaryMetrics.sort(sortByOrder)}
          breakdownPnlTimes={pnlTimes}
          breakdownRange={{
            ge: pnlBreakdown.ge,
            lt: pnlBreakdown.lt,
          }}
          {...{
            showMetricTitle,
            maxBreakdownHeaderLength,
            isExpanded,
            shouldExpand,
            shouldShowButterfly,
            reportLink,
            processId: "",
          }}
        />
        <Collapse in={isExpanded}>
          <SkuBreakdownStyles style={{ borderRadius: "0 0 10px 10px" }}>
            <ProcessPnLBreakdown
              pnlTimes={pnlTimes}
              breakdownType={BreakdownTypeEnum.shift}
            />
            <>
              {processPnlBreakdownRowData.length >= 1 &&
                processPnlBreakdownRowData?.map(
                  ({
                    processName,
                    processSummarySummaryMetrics,
                    processPnlBreakdown,
                    range,
                    processId,
                  }) => (
                    <BreakdownRow
                      key={processName}
                      processId={processId}
                      breakdownRange={range}
                      showMetricTitle={isMobileOrSmaller}
                      breakdownName={processName}
                      breakdownSummaryMetrics={processSummarySummaryMetrics}
                      breakdownPnlTimes={processPnlBreakdown}
                      isSkuBreakdown={true}
                      maxBreakdownHeaderLength={maxBreakdownHeaderLength}
                    />
                  )
                )}
            </>
          </SkuBreakdownStyles>

          {reportLink && <ReportButton href={reportLink} />}
        </Collapse>
      </MetricAndPnLBreakdownStyles>
    </>
  );
}
